import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Icon } from '../ui/Icon';
import { useShellSearch } from '../../features/shell/hooks';

/**
 * Header search (Stage 7 Phase 2A). Calls one permission-guarded endpoint per
 * entity, and only the ones this role can use — a role with neither
 * permission sees no search box at all (rendered by the caller).
 */
export function GlobalSearch({ canEmployees, canDevices }: { canEmployees: boolean; canDevices: boolean }) {
  const navigate = useNavigate();
  const [term, setTerm] = useState('');
  const [open, setOpen] = useState(false);
  const box = useRef<HTMLDivElement>(null);
  const { enabled, employees, devices } = useShellSearch(term, { employees: canEmployees, devices: canDevices });

  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (box.current && !box.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDoc);
    return () => document.removeEventListener('mousedown', onDoc);
  }, []);

  const emp = employees.data ?? [];
  const dev = devices.data ?? [];
  const loading = employees.isFetching || devices.isFetching;
  const none = enabled && !loading && emp.length === 0 && dev.length === 0;

  function go(path: string) {
    setOpen(false);
    setTerm('');
    navigate(path);
  }

  return (
    <div ref={box} className="relative w-72 max-w-full">
      <Icon
        name="search"
        className="pointer-events-none absolute left-2.5 top-1/2 -translate-y-1/2 text-[16px] text-ink-subtle"
      />
      <input
        type="search"
        aria-label="Search"
        value={term}
        onChange={(e) => {
          setTerm(e.target.value);
          setOpen(true);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => e.key === 'Escape' && setOpen(false)}
        placeholder={canEmployees ? 'Search employees' + (canDevices ? ' or devices…' : '…') : 'Search devices…'}
        className="h-9 w-full rounded-tight border border-line bg-canvas pl-8 pr-3 text-sm text-ink placeholder:text-ink-subtle focus-visible:border-accent"
      />
      {open && enabled && (
        <div
          role="listbox"
          className="absolute left-0 right-0 top-10 z-50 max-h-80 overflow-y-auto rounded-md border border-line bg-surface p-1 shadow-raised"
        >
          {loading && emp.length === 0 && dev.length === 0 && (
            <p className="px-3 py-2 text-xs text-ink-subtle">Searching…</p>
          )}
          {none && <p className="px-3 py-2 text-xs text-ink-subtle">No matches.</p>}
          {emp.length > 0 && (
            <>
              <p className="px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">Employees</p>
              {emp.map((e) => (
                <button
                  key={e.employee_id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => go(`/employees/${e.employee_id}`)}
                  className="flex w-full flex-col rounded px-3 py-1.5 text-left hover:bg-sunken"
                >
                  <span className="text-sm text-ink">{e.name}</span>
                  <span className="text-xs text-ink-subtle">
                    {e.employee_number}
                    {e.job_title ? ` · ${e.job_title}` : ''}
                  </span>
                </button>
              ))}
            </>
          )}
          {dev.length > 0 && (
            <>
              <p className="px-3 pb-0.5 pt-1.5 text-[10px] font-semibold uppercase tracking-wider text-ink-subtle">Devices</p>
              {dev.map((d) => (
                <button
                  key={d.device_id}
                  type="button"
                  role="option"
                  aria-selected={false}
                  onClick={() => go(`/devices/${d.device_id}`)}
                  className="flex w-full flex-col rounded px-3 py-1.5 text-left hover:bg-sunken"
                >
                  <span className="text-sm text-ink">{d.name}</span>
                  <span className="text-xs text-ink-subtle">{d.serial_number}</span>
                </button>
              ))}
            </>
          )}
        </div>
      )}
    </div>
  );
}
